/**
 * Probability masses and the exact count sampler behind the binomial and Poisson teaching models.
 * Masses use Loader's saddle-point form (as R's dbinom and dpois do), which stays accurate where
 * factorials overflow and log-factorial differences cancel.
 */

const LN_2PI = Math.log(2 * Math.PI);
/** The smallest normal double. */
const DBL_MIN = 2.2250738585072014e-308;

/** stirlerr(n) = ln(n!) - (n + 1/2) ln n + n - ln sqrt(2 pi) for integers 1..15, to double precision. */
const STIRLING_ERRORS = [
	Number.NaN,
	8.1061466795327258219670264e-2,
	4.1340695955409294093822081e-2,
	2.7677925684998339148789293e-2,
	2.0790672103765093111522772e-2,
	1.6644691189821192163194865e-2,
	1.3876128823070747998745727e-2,
	1.1896709945891770095055724e-2,
	1.0411265261972096497478567e-2,
	9.2554621827127329177286366e-3,
	8.3305634333628712564693187e-3,
	7.5736754879518407949720242e-3,
	6.9428401072095298656641527e-3,
	6.4089941880042070684396311e-3,
	5.9513701127588477356244160e-3,
	5.5547335519628013710386900e-3,
];

/** The Stirling-series error term of ln(n!) for an integer n >= 1. */
function stirlerr(n: number): number {
	if (n <= 15) return STIRLING_ERRORS[n]!;
	const nn = n * n;
	const s0 = 1 / 12;
	const s1 = 1 / 360;
	const s2 = 1 / 1260;
	const s3 = 1 / 1680;
	const s4 = 1 / 1188;
	if (n > 500) return (s0 - s1 / nn) / n;
	if (n > 80) return (s0 - (s1 - s2 / nn) / nn) / n;
	if (n > 35) return (s0 - (s1 - (s2 - s3 / nn) / nn) / nn) / n;
	return (s0 - (s1 - (s2 - (s3 - s4 / nn) / nn) / nn) / nn) / n;
}

/** x ln(x / m) + m - x, by a series where x is near m to avoid cancellation. */
function bd0(x: number, m: number): number {
	if (Math.abs(x - m) < 0.1 * (x + m)) {
		let v = (x - m) / (x + m);
		let s = (x - m) * v;
		if (Math.abs(s) < DBL_MIN) return s;
		let term = 2 * x * v;
		v *= v;
		for (let j = 1; j < 1000; j++) {
			term *= v;
			const next = s + term / (2 * j + 1);
			if (next === s) return next;
			s = next;
		}
	}
	return x * Math.log(x / m) + m - x;
}

/** ln P(X = k) for X ~ Binomial(n, p), k an integer. */
export function logBinomialMass(k: number, n: number, p: number): number {
	if (k < 0 || k > n) return -Infinity;
	if (p === 0) return k === 0 ? 0 : -Infinity;
	if (p === 1) return k === n ? 0 : -Infinity;
	const q = 1 - p;
	if (k === 0) return n * Math.log1p(-p);
	if (k === n) return n * Math.log(p);
	const lc = stirlerr(n) - stirlerr(k) - stirlerr(n - k) - bd0(k, n * p) - bd0(n - k, n * q);
	return lc - 0.5 * (LN_2PI + Math.log(k) + Math.log1p(-k / n));
}

/** ln P(X = k) for X ~ Poisson(rate), k an integer. */
export function logPoissonMass(k: number, rate: number): number {
	if (k < 0) return -Infinity;
	if (k === 0) return -rate;
	return -stirlerr(k) - bd0(k, rate) - 0.5 * (LN_2PI + Math.log(k));
}

/** A count distribution with a log-concave mass on the integers lo..hi (hi may be Infinity). */
export interface LogConcaveCounts {
	lo: number;
	hi: number;
	/** The largest of the most probable counts. */
	mode: number;
	sd: number;
	logMass(k: number): number;
}

/**
 * Exact counts by rejection from a hat that bounds a log-concave mass: flat at the mode's mass over
 * the mode ± max(2, ceil(sd)), and geometric beyond, continuing the mass's last step ratio, which
 * log-concavity makes an upper bound. Each draw takes about 1.3 tries whatever the parameters.
 */
export function sampleCounts(dist: LogConcaveCounts, uniform: () => number, size: number): number[] {
	const width = Math.max(2, Math.ceil(dist.sd));
	const a = Math.max(dist.lo, dist.mode - width);
	const b = Math.min(dist.hi, dist.mode + width);
	const logPeak = dist.logMass(dist.mode);
	const center = (b - a + 1) * Math.exp(logPeak);
	const right = b < dist.hi ? tail(dist.logMass(b), dist.logMass(b) - dist.logMass(b - 1)) : undefined;
	const left = a > dist.lo ? tail(dist.logMass(a), dist.logMass(a) - dist.logMass(a + 1)) : undefined;
	const total = center + (right?.mass ?? 0) + (left?.mass ?? 0);
	const out = new Array<number>(size);
	for (let i = 0; i < size; ) {
		const u = uniform() * total;
		let k: number;
		let logHat: number;
		if (u < center) {
			k = a + Math.floor(uniform() * (b - a + 1));
			logHat = logPeak;
		} else {
			const side = u < center + (right?.mass ?? 0) ? right! : left!;
			const steps = 1 + Math.floor(Math.log(1 - uniform()) / side.logStep);
			k = side === right ? b + steps : a - steps;
			if (k < dist.lo || k > dist.hi || !Number.isSafeInteger(k)) continue;
			logHat = side.logStart + steps * side.logStep;
		}
		if (Math.log(uniform()) + logHat <= dist.logMass(k)) out[i++] = k;
	}
	return out;
}

/** A geometric tail starting one step past a count with mass exp(logStart), shrinking by exp(logStep) a step. */
function tail(logStart: number, logStep: number): { logStart: number; logStep: number; mass: number } {
	const step = Math.min(logStep, -Number.EPSILON);
	return { logStart, logStep: step, mass: (Math.exp(logStart + step) / -Math.expm1(step)) };
}
